import { Events } from '@carvana/vdp-events';
import { useContext, useEffect } from 'react';
import TradeContext from '~/context/common/TradeContext';
import type { TradeInPreviewAcquisition } from '~/contracts/common';
import type { EventDetail } from '~/contracts/vdp';
import { useEventElements } from '~/hooks/vdp';
import {
  addEventListeners,
  filterLocalEvents,
  removeEventListeners
} from '~/utilities/browserEvents';
import FinanceContext from './FinanceContext';

/**
 * This hook synchronizes Price Details' local state with the Vehicle Details
 * Page's Finance state. We do this to separate concerns.
 *
 * The local state has more data and complexity than the global state. Instead
 * of lifting the complexity to a global level, the global state has access to
 * the bare minimum required by components external to Price Details.
 *
 * We synchronize the two state repositories by listening to changes in the
 * data.
 * */

function useEventListeners() {
  const {
    setEstimatedAmountFinanced,
    setIsLoaded,
    setError,
    setLoading,
    setTriggerUserFinancingRequest
  } = useContext(FinanceContext);
  const { setActiveTrade, setTradeInApplied } = useContext(TradeContext);
  const eventElements = useEventElements();

  useEffect(() => {
    // Active Trade-in
    const handleActiveTrade = filterLocalEvents<TradeInPreviewAcquisition>(
      ev => {
        setActiveTrade(ev.detail.data);
      }
    );

    // Estimated Amount Financed
    const handleEstimatedAmountFinanced = filterLocalEvents<number>(ev => {
      setEstimatedAmountFinanced(ev.detail.data);
      setIsLoaded(true);
    });

    // Error
    const handleIsError = filterLocalEvents<boolean>(ev => {
      setError(ev.detail.data);
    });

    // Loading
    const handleIsLoading = filterLocalEvents<boolean>(ev => {
      setLoading(ev.detail.data);
    });

    // Trade-in Applied
    const handleTradeInApplied = filterLocalEvents<boolean>(ev => {
      setTradeInApplied(ev.detail.data);
    });

    // User Financing
    const handleUserFinancing = filterLocalEvents<any>(() => {
      setTriggerUserFinancingRequest(Date.now());
    });

    addEventListeners<CustomEvent<EventDetail>>(eventElements, [
      [Events.ActiveTrade, handleActiveTrade],
      [Events.EstimatedAmountFinanced, handleEstimatedAmountFinanced],
      [Events.FinanceIsError, handleIsError],
      [Events.FinanceIsLoading, handleIsLoading],
      [Events.IsTradeInApplied, handleTradeInApplied],
      [Events.UserFinancing, handleUserFinancing]
    ]);

    return () => {
      removeEventListeners<CustomEvent<EventDetail>>(eventElements, [
        [Events.ActiveTrade, handleActiveTrade],
        [Events.EstimatedAmountFinanced, handleEstimatedAmountFinanced],
        [Events.FinanceIsError, handleIsError],
        [Events.FinanceIsLoading, handleIsLoading],
        [Events.IsTradeInApplied, handleTradeInApplied],
        [Events.UserFinancing, handleUserFinancing]
      ]);
    };
  }, [
    eventElements,
    setActiveTrade,
    setError,
    setEstimatedAmountFinanced,
    setIsLoaded,
    setLoading,
    setTradeInApplied,
    setTriggerUserFinancingRequest
  ]);
}

export default useEventListeners;
